import { Locale } from "../Models/Localization/Locale";

/**
 * A resource which provides a translation in each supported language.
 */
class ResourceItem {
    /**
     * The unique translation key.
     */
    public key: string;

    /**
     * The English translation text.
     */
    public en: string;

    /**
     * The French translation text.
     */
    public fr: string;

    /**
     * The Spanish translation text.
     */
    public es: string;

    /**
     * Construct an instance.
     * @param key The unique translation key.
     * @param en The English translation text.
     * @param fr The French translation text.
     * @param es The Spanish translation text.
     */
    constructor(key: string = "", en: string = "", fr: string = "", es: string = "") {
        this.key = key;
        this.en = en;
        this.fr = fr;
        this.es = es;
    }
}

/**
 * A managed collection of {@link ResourceItem} translation resources.
 */
class ResourceCollection {
    /**
     * The translation resources in the collection.
     */
    private resources: { [key: string]: ResourceItem } = {};

    /**
     * Construct an instance.
     * @param resources The translation resources in the collection.
     */
    constructor(resources: ResourceItem[]) {
        for (const resource of resources)
            this.add(resource);
    }

    /**
     * Get a translation resource by key.
     * @param key The unique translation key.
     * @returns Returns the matching resource, or null if no match was found.
     */
    public get(key: string): ResourceItem | null {
        return this.resources[key] ?? null;
    }

    /**
     * Add a translation resource to the collection.
     * @param resource The translation resource to add.
     */
    public add(resource: ResourceItem): void {
        if (this.resources[resource.key])
            console.warn(`The '${resource.key}' static resource is defined more than once.`);

        this.resources[resource.key] = resource;
    }
}

/**
 * Provides access to predefined translations.
 */
class ResourceProvider {
    /**
     * The underlying translation resource collection.
     */
    private resources: ResourceCollection;

    /**
     * The default locale code for which to get translations.
     */
    private locale: Locale;

    /**
     * Construct an instance.
     * @param resources The underlying translation resource collection.
     * @param locale The default locale code for which to get translations.
     */
    constructor(resources: ResourceCollection, locale?: Locale) {
        this.resources = resources;
        this.locale = locale || sessionStore.locale;
    }

    /**
     * Get a translation from the provider.
     * @param key The unique translation key.
     * @param tokens An object containing token values to substitute in the string.
     * @param locale The locale code for which to get translations, or null for the current locale. This must be one of the preloaded locales.
     */
    public GetString(key: string, tokens: object = {}, locale: Locale = null): string {
        // get resource
        const item = this.resources.get(key);
        if (!item)
            return "";

        // get raw text
        let text: string;
        switch (locale || this.locale) {
            case Locale.fr:
                text = item.fr ?? "";
                break;

            case Locale.es:
                text = item.es ?? "";
                break;

            default:
                text = item.en ?? "";
                break;
        }

        // apply tokens
        for (const key of Object.keys(tokens || {}))
            text = text.replaceAll("{{" + key + "}}", tokens[key], true);

        return text;
    }
}

export { ResourceCollection, ResourceProvider };
